/**
 * Static boot loader shared by the SPA shells (console, interview).
 *
 * The loader must paint before the app bundle — and therefore before React,
 * Tailwind, and the theme tokens — exists, so everything here is inlined into
 * index.html at build time by `bootLoaderPlugin`: a theme-resolving script, a
 * self-contained stylesheet, and the loader markup inside `#root` (which
 * React replaces on mount, so no teardown is needed).
 *
 * `AppLoadingScreen`-style in-app loaders should render the same mark from
 * `BOOT_LOADER_BARS` so the bundle-load → data-load → app handoff reads as
 * one continuous state.
 */

export interface BootLoaderBar {
  height: number;
  delaySeconds: number;
  accent?: boolean;
}

/** The waveform mark from the favicon, as bar geometry. */
export const BOOT_LOADER_BARS: readonly BootLoaderBar[] = [
  { height: 12, delaySeconds: 0 },
  { height: 34, delaySeconds: 0.12 },
  { height: 20, delaySeconds: 0.24 },
  { height: 44, delaySeconds: 0.36, accent: true },
  { height: 16, delaySeconds: 0.48 },
];

// Plain-hex fallbacks precede each oklch() value: on browsers without oklch
// support (pre Chrome 111 / Safari 15.4) the oklch declaration is dropped and
// the hex keeps the loader from painting on a transparent background. The
// pairs mirror the app theme tokens (--color-background / --color-primary).
const LIGHT_BG_HEX = '#f8f8f8';
const LIGHT_BG = 'oklch(0.98 0 0)';
const DARK_BG_HEX = '#181818';
const DARK_BG = 'oklch(0.21 0 0)';
const LIGHT_ACCENT_HEX = '#445dad';
const LIGHT_ACCENT = 'oklch(0.5 0.13 268)';
const DARK_ACCENT_HEX = '#84a1f0';
const DARK_ACCENT = 'oklch(0.72 0.12 268)';

// Resolves the theme before first paint using the same storage key and system
// fallback the app's ThemeProvider applies once React mounts.
const THEME_SCRIPT = `
      (function () {
        try {
          var theme = localStorage.getItem('sara-ui-theme');
          var dark =
            theme === 'dark' ||
            ((theme === 'system' || !theme) &&
              window.matchMedia('(prefers-color-scheme: dark)').matches);
          document.documentElement.classList.add(dark ? 'dark' : 'light');
        } catch (_) {
          /* storage unavailable — keep the light default */
        }
      })();`;

function barRules(): string {
  return BOOT_LOADER_BARS.map((bar, i) => {
    const lines = [`height: ${bar.height}px;`];
    if (bar.delaySeconds > 0) lines.push(`animation-delay: ${bar.delaySeconds}s;`);
    if (bar.accent) {
      lines.push(`background: ${LIGHT_ACCENT_HEX};`, `background: ${LIGHT_ACCENT};`);
    }
    const dark = bar.accent
      ? `\n      html.dark .boot-loader-wave span:nth-child(${i + 1}) {
        background: ${DARK_ACCENT_HEX};
        background: ${DARK_ACCENT};
      }`
      : '';
    return `      .boot-loader-wave span:nth-child(${i + 1}) {
        ${lines.join('\n        ')}
      }${dark}`;
  }).join('\n');
}

function styles(): string {
  return `
      html.dark {
        background: ${DARK_BG_HEX};
        background: ${DARK_BG};
      }
      .boot-loader {
        position: fixed;
        inset: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 24px;
        background: ${LIGHT_BG_HEX};
        background: ${LIGHT_BG};
        font-family: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif;
      }
      html.dark .boot-loader {
        background: ${DARK_BG_HEX};
        background: ${DARK_BG};
      }
      .boot-loader-wave {
        display: flex;
        align-items: center;
        gap: 5px;
        height: 44px;
      }
      .boot-loader-wave span {
        width: 6px;
        border-radius: 4px;
        background: rgba(36, 33, 29, 0.3);
        animation: boot-loader-eq 1.1s ease-in-out infinite;
      }
      html.dark .boot-loader-wave span {
        background: rgba(233, 233, 233, 0.3);
      }
${barRules()}
      .boot-loader-text {
        font-size: 13px;
        letter-spacing: 0.02em;
        color: rgba(36, 33, 29, 0.55);
      }
      html.dark .boot-loader-text {
        color: rgba(233, 233, 233, 0.55);
      }
      @keyframes boot-loader-eq {
        0%,
        100% {
          transform: scaleY(0.55);
        }
        50% {
          transform: scaleY(1);
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .boot-loader-wave span {
          animation: none;
        }
      }`;
}

const ROOT_PLACEHOLDER = '<div id="root"></div>';

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function markup(message: string): string {
  const spans = BOOT_LOADER_BARS.map(() => '<span></span>').join('');
  // role="status" with no aria-label so the visible message is the accessible name.
  return `<div class="boot-loader" role="status">
        <div class="boot-loader-wave" aria-hidden="true">${spans}</div>
        <div class="boot-loader-text">${escapeHtml(message)}</div>
      </div>`;
}

export interface BootLoaderOptions {
  /** Visible loading message, e.g. "Loading console…". */
  message: string;
}

/**
 * Vite plugin injecting the boot loader into index.html. Typed structurally
 * (name + transformIndexHtml) so this package needs no dependency on vite.
 */
export function bootLoaderPlugin({ message }: BootLoaderOptions): {
  name: string;
  transformIndexHtml: (html: string) => string;
} {
  return {
    name: 'sara-boot-loader',
    transformIndexHtml(html: string): string {
      const HEAD_CLOSE = '</head>';
      for (const placeholder of [ROOT_PLACEHOLDER, HEAD_CLOSE]) {
        if (!html.includes(placeholder)) {
          throw new Error(
            `bootLoaderPlugin: index.html has no ${placeholder} placeholder to inject into`
          );
        }
      }
      return html
        .replace(
          HEAD_CLOSE,
          `  <script>${THEME_SCRIPT}\n    </script>\n    <style>${styles()}\n    </style>\n  </head>`
        )
        .replace(ROOT_PLACEHOLDER, `<div id="root">\n      ${markup(message)}\n    </div>`);
    },
  };
}
